import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Divider,
  Alert,
  Progress,
  Space,
  Typography,
  TreeSelect,
} from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, TeamOutlined, ApartmentOutlined, IdcardOutlined } from '@ant-design/icons';
import type { UserFormProps, UserFormData, OrganizationTreeNode } from '../types';
import { DesignTokens, getPasswordStrengthColor } from '../DesignTokens';
import {
  validateUsername,
  validatePhone,
  validateEmail,
  validatePassword,
  getPasswordStrength,
} from '../utils';

const { Text } = Typography;

export const UserForm: React.FC<UserFormProps> = ({
  open,
  mode,
  initialValues,
  availableRoles = [],
  organizationTree = [],
  availablePositions = [],
  onOk,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [passwordStrength, setPasswordStrength] = useState<
    'weak' | 'medium' | 'strong'
  >('weak');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
      setPassword('');
      setPasswordStrength('weak');
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onOk?.(values);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordStrength(getPasswordStrength(pwd));
  };

  const getStrengthPercent = (): number => {
    if (passwordStrength === 'weak') return 33;
    if (passwordStrength === 'medium') return 66;
    return 100;
  };

  const getStrengthText = (): string => {
    if (passwordStrength === 'weak') return '弱';
    if (passwordStrength === 'medium') return '中';
    return '强';
  };

  const isEditMode = mode === 'edit';

  // 将组织树转换为TreeSelect需要的格式
  const convertToTreeData = (nodes: OrganizationTreeNode[]): any[] => {
    return nodes.map(node => ({
      title: node.name,
      value: node.id,
      key: node.id,
      children: node.children ? convertToTreeData(node.children) : undefined,
    }));
  };

  const treeData = useMemo(() => convertToTreeData(organizationTree), [organizationTree]);

  return (
    <Modal
      data-testid="user-modal-form"
      title={isEditMode ? '编辑用户' : '新建用户'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={640}
      okText={isEditMode ? '确定' : '创建'}
      cancelText="取消"
      styles={{
        header: {
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.lg}px ${DesignTokens.spacing.xl}px`,
        },
        body: {
          padding: DesignTokens.spacing.xl,
        },
        footer: {
          background: DesignTokens.colors.gray2,
          borderTop: `1px solid ${DesignTokens.colors.gray4}`,
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'active',
          roleIds: [],
        }}
      >
        {isEditMode && (
          <Alert
            message="编辑提示"
            description="用户名创建后不可修改"
            type="info"
            showIcon
            style={{ marginBottom: DesignTokens.spacing.lg }}
          />
        )}

        {/* 基本信息 */}
        <Divider orientation="left">基本信息</Divider>

        <Form.Item
          label="用户名"
          name="username"
          rules={[
            { required: true, message: '请输入用户名' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!validateUsername(value)) {
                  return Promise.reject(
                    new Error('用户名格式不正确（4-20个字符，仅限字母数字下划线）')
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            data-testid="user-input-username"
            prefix={<UserOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            placeholder="4-20个字符，字母数字下划线"
            disabled={isEditMode}
            maxLength={20}
          />
        </Form.Item>

        <Form.Item
          label="真实姓名"
          name="realName"
          rules={[{ required: true, message: '请输入真实姓名' }]}
        >
          <Input
            data-testid="user-input-realname"
            prefix={<UserOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            placeholder="请输入真实姓名"
            maxLength={50}
          />
        </Form.Item>

        <Form.Item
          label="手机号"
          name="phone"
          rules={[
            { required: true, message: '请输入手机号' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!validatePhone(value)) {
                  return Promise.reject(new Error('手机号格式不正确'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            data-testid="user-input-phone"
            prefix={<PhoneOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            placeholder="请输入11位手机号"
            maxLength={11}
          />
        </Form.Item>

        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!validateEmail(value)) {
                  return Promise.reject(new Error('邮箱格式不正确'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            data-testid="user-input-email"
            prefix={<MailOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            placeholder="请输入邮箱（可选）"
            maxLength={100}
          />
        </Form.Item>

        {/* 组织分配 */}
        <Divider orientation="left">组织分配</Divider>

        <Form.Item label="所属组织" name="organizationId">
          <TreeSelect
            data-testid="user-select-organization"
            placeholder="请选择所属组织（可选）"
            treeData={treeData}
            allowClear
            showSearch
            treeDefaultExpandAll
            treeLine={{ showLeafIcon: false }}
            treeNodeFilterProp="title"
            suffixIcon={<ApartmentOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label="岗位" name="positionId">
          <Select
            data-testid="user-select-position"
            placeholder="请选择岗位（可选）"
            allowClear
            showSearch
            optionFilterProp="label"
            suffixIcon={<IdcardOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            options={availablePositions.map(pos => ({
              label: pos.name,
              value: pos.id,
            }))}
          />
        </Form.Item>

        <Form.Item label="部门" name="department">
          <Input
            data-testid="user-input-department"
            prefix={<TeamOutlined style={{ color: DesignTokens.colors.gray7 }} />}
            placeholder="请输入部门（可选）"
            maxLength={50}
          />
        </Form.Item>

        {/* 角色分配 */}
        <Divider orientation="left">角色分配</Divider>

        <Form.Item
          label="角色"
          name="roleIds"
          rules={[{ required: true, message: '请至少选择一个角色' }]}
        >
          <Select
            data-testid="user-select-role"
            mode="multiple"
            placeholder="请选择角色"
            options={availableRoles.map((role) => ({
              label: role.name,
              value: role.id,
            }))}
            maxTagCount="responsive"
          />
        </Form.Item>

        {/* 密码设置（仅新建时） */}
        {!isEditMode && (
          <>
            <Divider orientation="left">密码设置</Divider>

            <Form.Item
              label="密码"
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (!validatePassword(value)) {
                      return Promise.reject(
                        new Error('密码至少8位，包含大小写字母和数字')
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                data-testid="user-input-password"
                placeholder="至少8位，包含大小写字母和数字"
                onChange={handlePasswordChange}
                maxLength={32}
              />
            </Form.Item>

            {password && (
              <div style={{ marginBottom: DesignTokens.spacing.lg }}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ ...DesignTokens.typography.bodySmall }}
                    >
                      密码强度:
                    </Text>
                    <Text
                      style={{
                        ...DesignTokens.typography.bodySmall,
                        color: getPasswordStrengthColor(passwordStrength),
                        fontWeight: 500,
                      }}
                    >
                      {getStrengthText()}
                    </Text>
                  </div>
                  <Progress
                    percent={getStrengthPercent()}
                    strokeColor={getPasswordStrengthColor(passwordStrength)}
                    showInfo={false}
                    size="small"
                  />
                </Space>
              </div>
            )}

            <Form.Item
              label="确认密码"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password data-testid="user-input-confirm-password" placeholder="请再次输入密码" maxLength={32} />
            </Form.Item>
          </>
        )}

        {/* 状态设置 */}
        <Divider orientation="left">状态设置</Divider>

        <Form.Item label="用户状态" name="status">
          <Radio.Group data-testid="user-radio-status">
            <Radio data-testid="user-radio-status-active" value="active">正常</Radio>
            <Radio data-testid="user-radio-status-disabled" value="disabled">禁用</Radio>
          </Radio.Group>
        </Form.Item>

        <Alert
          message={
            <span style={{ ...DesignTokens.typography.bodySmall }}>
              禁用后用户将无法登录系统
            </span>
          }
          type="warning"
          showIcon
        />
      </Form>
    </Modal>
  );
};
